import {
    Table, TableCell, TableRow, Typography,
} from "@mui/material";
import cx from "classnames";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "../../../../../hooks/useTranslate";
import { useStudents } from "../../../../../context/students";
import "./RecommendedSubjects.css";
import { useGetSubjects } from "../../../../../hooks/useGetSubjects";
import Preloader from "../../../../../components/Preloader";
import { useMutation } from "@tanstack/react-query";
import axiosManager from "../../../../../services/axiosManager";
import SnackbarToast from "../../../../../components/SnackbarToast";
import EmptyContent from "../../../../../components/EmptyContent";

const RECOMMENDATION_STATUS = "Recommendation";
const APPROVED_STATUS = "Approved";
const REJECTED_STATUS = "Rejected";
const CONFIRMED_STATUS = "Confirmed";

const RecommendedSubjects = (props) => {
    const { isCompleted, onComplete } = props;
    const { t } = useTranslate();
    const { activeStudent, activeStudentId } = useStudents();
    const navigate = useNavigate();
    const {
        isLoading, isRefetching, subjects, refetch,
    } = useGetSubjects(activeStudentId);

    const subjectsToShow = (subjects || []).filter((subject) => [
        RECOMMENDATION_STATUS,
        APPROVED_STATUS,
        REJECTED_STATUS,
        CONFIRMED_STATUS,
    ].includes(subject.status));

    const {
        mutate: updateSubject, reset: resetUpdateSubject,
        isPending: isUpdatingSubject, isSuccess: isSuccessUpdateSubject, error: errorUpdateSubject,
    } = useMutation({
        mutationFn: ({ subjectId, status }) => axiosManager.patch$(`/parent/api/students/${activeStudentId}/subjects/${subjectId}`, { status }),
        onSuccess: () => {
            refetch();
        },
    });
    const {
        mutate: updateSubjects, reset: resetUpdateSubjects,
        isPending: isUpdatingSubjects, isSuccess: isSuccessUpdateSubjects, error: errorUpdateSubjects,
    } = useMutation({
        mutationFn: (body) => axiosManager.patch$(`/parent/api/students/${activeStudentId}/subjects`, body),
        onSuccess: () => {
            refetch();
            onComplete?.();
        },
    });

    const isAllChecked = subjectsToShow.every((subject) => [APPROVED_STATUS, REJECTED_STATUS, CONFIRMED_STATUS].includes(subject.status));
    const isAllApproved = subjectsToShow.every((subject) => [APPROVED_STATUS, CONFIRMED_STATUS].includes(subject.status));

    const onApproveAll = () => {
        updateSubjects(subjectsToShow.map((subject) => ({ subjectId: subject.subjectId, status: APPROVED_STATUS })));
    };
    const onApprove = (subjectId) => () => {
        updateSubject({ subjectId, status: APPROVED_STATUS });
    };
    const onReject = (subjectId) => () => {
        updateSubject({ subjectId, status: REJECTED_STATUS });
    };
    const onBookConsultationCall = () => {
        navigate(`/consultation-call/${activeStudentId}`);
    };
    const onConfirm = () => {
        updateSubjects(subjectsToShow.map((subject) => ({ subjectId: subject.subjectId, status: CONFIRMED_STATUS })));
    };

    return (
        <div className="recommended-subjects">
            <div className="recommended-subjects__header">
                <div>
                    <Typography variant="h4" component="div" className="text-primary">
                        {`${activeStudent.forename}${t("courseSelection.recommendedSubjects")}`}
                    </Typography>
                    <Typography variant="body1" component="div" className="">
                        {t("courseSelection.recommendedSubjectsDescription")}
                    </Typography>
                </div>
                <button
                    className="recommended-subjects__approve-all text-link"
                    disabled={isAllApproved}
                    onClick={onApproveAll}
                >
                    {t("courseSelection.approveAllSubjects")}
                </button>
            </div>
            <Preloader isLoading={isLoading}>
                <Preloader isLoading={isRefetching || isUpdatingSubject || isUpdatingSubjects} overflow showInitiallyOverflow>
                    <EmptyContent
                        data={subjectsToShow}
                        text={(
                            <div className="p-4 mt-4">{t("courseSelection.noSubjectsText")}</div>
                        )}
                    >
                        <Table className="subjects-table">
                            {subjectsToShow.map((subject) => (
                                <TableRow
                                    key={subject.subjectId}
                                    className={cx(
                                        { "subjects-table__row-approved": [APPROVED_STATUS, CONFIRMED_STATUS].includes(subject.status) },
                                        { "subjects-table__row-rejected": subject.status === REJECTED_STATUS },
                                    )}
                                >
                                    <TableCell>
                                        <span className="subjects-table__title">{t("courseSelection.course")}</span>
                                        <span className="subjects-table__value">{subject.subjectName}</span>
                                    </TableCell>
                                    {!isCompleted && (
                                        <TableCell>
                                            <span className="subjects-table__title">
                                                {t("courseSelection.decision")}
                                            </span>
                                            <div className="flex align-center gap-4">
                                                <Button
                                                    variant="outlined"
                                                    className="subjects-table__button subjects-table__button-approve"
                                                    onClick={onApprove(subject.subjectId)}
                                                    disabled={subject.status === CONFIRMED_STATUS}
                                                >
                                                    {t("courseSelection.approve")}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    className="subjects-table__button subjects-table__button-reject"
                                                    onClick={onReject(subject.subjectId)}
                                                    disabled={subject.status === CONFIRMED_STATUS}
                                                >
                                                    {t("courseSelection.reject")}
                                                </Button>
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </Table>
                    </EmptyContent>
                </Preloader>
            </Preloader>
            {(!isCompleted && isAllChecked && subjectsToShow.length > 0) && (
                <>
                    <div className="divider mb-8" />
                    <Typography variant="h4" component="div" className="text-primary">
                        {isAllApproved ? t("courseSelection.thankForReviewing") : t("courseSelection.speakToAdvisor")}
                    </Typography>
                    <Typography variant="body1" component="div" className="">
                        {isAllApproved ? t("courseSelection.thankForReviewingDescription") : t("courseSelection.speakToAdvisorDescription")}
                    </Typography>
                    {isAllApproved ? null : (
                        <Button variant="contained" className="button-accent mt-4 button-margin" onClick={onBookConsultationCall}>
                            {t("courseSelection.bookConsultationCall")}
                        </Button>
                    )}
                    <Button variant="contained" className="button-accent mt-4" onClick={onConfirm}>
                        {t("courseSelection.confirmSubjects")}
                    </Button>
                </>
            )}
            <SnackbarToast
                open={isSuccessUpdateSubject || !!errorUpdateSubject}
                isSuccess={isSuccessUpdateSubject}
                isError={errorUpdateSubject}
                onClose={resetUpdateSubject}
            >
                {isSuccessUpdateSubject
                    ? t("courseSelection.successUpdateSubject")
                    : `${t("courseSelection.errorUpdateSubject")} (status ${errorUpdateSubject?.response?.status})`}
            </SnackbarToast>
            <SnackbarToast
                open={isSuccessUpdateSubjects || !!errorUpdateSubjects}
                isSuccess={isSuccessUpdateSubjects}
                isError={errorUpdateSubjects}
                onClose={resetUpdateSubjects}
            >
                {isSuccessUpdateSubjects
                    ? t("courseSelection.successUpdateSubjects")
                    : `${t("courseSelection.errorUpdateSubjects")} (status ${errorUpdateSubjects?.response?.status})`}
            </SnackbarToast>
        </div>
    );
};

export default RecommendedSubjects;
